import React from 'react';
import styled from 'styled-components';
import { bool, func, number, string } from 'prop-types';
import { nullFunction } from '../../utils/utils';
import { variant } from 'styled-system';

import Box from './Box';
import Question from './icons/QuestionCircle';
import QuestionCircle from './icons/QuestionCircle';

const StyledWrapper = styled('span')(
  ({ theme }) => ({
    display: 'inline-flex',
    height: '24px',
    lineHeight: '24px',
    alignItems: 'center',
    position: 'relative',
    background: theme.colors.white,
    boxShadow: `0 0 0 2px ${theme.colors.greyVerylight} inset`,
    fontSize: '12px',
    borderRadius: theme.radii[3],
    color: theme.colors.dark,
    '&:not(:last-child)': {
      marginRight: theme.space[2]
    },
    paddingRight: '1px',
    paddingLeft: theme.space[2],
    fontWeight: 700,
    marginTop: '4px',
    marginBottom: '4px',
    marginRight: 'auto'
  }),
  ({ theme }) =>
    variant({
      variants: {
        highlighted: {
          boxShadow: `0 0 0 2px ${theme.colors.mainLight} inset`
        },
        mandatoryMissing: {
          color: theme.colors.grey600,
          boxShadow: 'none',
          border: `1px dashed ${theme.colors.greyer}`,
          padding: '5px 24px 5px 10px'
        },
        mandatoryExisting: {
          border: `2px solid ${theme.colors.mainDark}`
        }
      }
    })
);

const StyledLevel = styled('span')(
  ({ theme }) => ({
    background: theme.colors.greyVerylight,
    fontWeight: '700',
    height: '22px',
    width: '22px',
    borderRadius: theme.radii[3],
    color: theme.colors.dark,
    textAlign: 'center',
    marginLeft: theme.space[1],
    marginRight: '-1px'
  }),
  ({ theme }) =>
    variant({
      variants: {
        default: {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0
        },
        highlighted: {
          backgroundColor: theme.colors.mainLight
        },
        mandatoryMissing: {
          backgroundColor: theme.colors.gray150,
          color: theme.colors.grey600,
          fontWeight: '600'
        },
        mandatoryExisting: {
          backgroundColor: theme.colors.mainDark,
          color: theme.colors.white
        }
      }
    })
);

/**
 * @typedef {object} Skill
 * @type {boolean} Skill.displayMandatorySkill
 * @type {boolean} Skill.isMandatory
 * @type {boolean} Skill.isMandatorySkillMissing
 * @type {boolean} Skill.highlighted
 *
 * @param {Skill} skill
 */
const getVariant = skill => {
  if (skill.displayMandatorySkill && skill.isMandatory && !skill.isMandatorySkillMissing) {
    return 'mandatoryExisting';
  } else if (skill.displayMandatorySkill && skill.isMandatorySkillMissing) {
    return 'mandatoryMissing';
  } else if (skill.highlighted) {
    return 'highlighted';
  }

  return 'default';
};

const SkillReadonly = ({
  text,
  level,
  highlighted,
  displayMandatorySkill,
  isMandatory,
  isMandatorySkillMissing,
  tooltipMandatorySkill,
  onClick
}) => {
  const variant = getVariant({ displayMandatorySkill, isMandatory, isMandatorySkillMissing, highlighted });

  /**
   * @param {JSX.Element} children
   * @returns {JSX.Element}
   */
  // eslint-disable-next-line react/prop-types
  const SkillComponent = ({ children }) => (
    <StyledWrapper variant={variant} data-cy="skill-content" onClick={onClick}>
      {children}
      <StyledLevel variant={variant}>{level}</StyledLevel>
    </StyledWrapper>
  );

  if (isMandatory && isMandatorySkillMissing && !!tooltipMandatorySkill) {
    return (
      <SkillComponent>
        <span className="tooltip" data-text={tooltipMandatorySkill}>
          {text}
        </span>
        <Box position="absolute" right="6px" top="50%" transform="translateY(-50%)" height="12px" lineHeight="12px">
          <span className="tooltip" data-text={tooltipMandatorySkill}>
            <QuestionCircle color="#B4B5B8" fontSize="12px" />
          </span>
        </Box>
      </SkillComponent>
    );
  }

  return <SkillComponent>{text}</SkillComponent>;
};

SkillReadonly.defaultProps = {
  displayMandatorySkill: false,
  isMandatory: false,
  isMandatorySkillMissing: false,
  highlighted: false,
  onClick: nullFunction
};

SkillReadonly.propTypes = {
  displayMandatorySkill: bool,
  tooltipMandatorySkill: string,
  isMandatory: bool,
  isMandatorySkillMissing: bool,
  highlighted: bool,
  text: string,
  level: number,
  onClick: func
};

export default SkillReadonly;
